import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import { AnalysisResult } from '../../../Clients/ImportClients/useAnalyseCSV';
import { ColumnMapping } from '../../../Clients/ImportClients/autoMap';
import { getEntitiesDataItems } from '../../../../store/entities/selectors';
import { ClientEntity, ProfessionalEntity } from '../../../../store/entities/state';
import { getGeneralSettings } from '../../../../store/settings/selectors';
import { ApplicationState } from '../../../../store';
import StyledTable from '../../../../components/StyledTable';
import { ImportAppointmentOptions } from './ImportAppointmentOptions';
import { parseAppointmentRow, ParsedAppointmentRow, ParseContext, RowOutcome, resolveRoom } from './parseAppointmentRow';

interface Props {
    result: AnalysisResult;
    mapping: ColumnMapping;
    options: ImportAppointmentOptions;
}

const OUTCOME_COLOR: Record<RowOutcome, string> = {
    create: 'var(--appGreen)',
    'new-client': 'var(--warning-color)',
    skip: 'var(--appLigthGrey)',
    error: 'var(--danger-color)',
};

const OUTCOME_TK: Record<RowOutcome, TK> = {
    create: TK.importAppointmentsStatusCreate,
    'new-client': TK.importAppointmentsStatusNewClient,
    skip: TK.importAppointmentsStatusSkip,
    error: TK.importAppointmentsStatusError,
};

const ERROR_TK: Record<string, TK> = {
    invalidDate: TK.importAppointmentsErrorInvalidDate,
    noProfessional: TK.importAppointmentsErrorNoProfessional,
    unknownProfessional: TK.importAppointmentsErrorUnknownProfessional,
    noClient: TK.importAppointmentsErrorNoClient,
    clientNotFound: TK.importAppointmentsErrorClientNotFound,
};

const AppointmentPreviewStep: React.FC<Props> = ({ result, mapping, options }) => {
    const t = useTranslations();
    const clients = useSelector<ApplicationState, ClientEntity[]>(getEntitiesDataItems<ClientEntity>('clients'));
    const professionals = useSelector<ApplicationState, ProfessionalEntity[]>(getEntitiesDataItems<ProfessionalEntity>('professionals'));
    const generalSettings = useSelector(getGeneralSettings);

    const serviceNames = React.useMemo((): Record<string, string> => {
        const map: Record<string, string> = {};
        for (const [key, s] of Object.entries(generalSettings?.services ?? {})) {
            map[key] = s.name || key;
        }
        return map;
    }, [generalSettings]);

    // Keep a ref so the parse always reads the latest lookup data without
    // treating Redux entity churn as a reason to re-parse.
    const ctxRef = React.useRef<ParseContext>({ mapping, options, clients, professionals, generalSettings, serviceNames });
    ctxRef.current = { mapping, options, clients, professionals, generalSettings, serviceNames };

    const [parsed, setParsed] = React.useState<ParsedAppointmentRow[] | null>(null);

    React.useEffect(() => {
        setParsed(null);
        const id = setTimeout(() => {
            setParsed(result.allRows.map((row, i) => parseAppointmentRow(row, i, ctxRef.current)));
        }, 0);
        return () => clearTimeout(id);
    // Re-parse only when the import configuration changes, not on every Redux entity update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result.allRows, mapping, options]);

    const counts = React.useMemo(() => !parsed ? null : ({
        create: parsed.filter(r => r.outcome === 'create').length,
        skip: parsed.filter(r => r.outcome === 'skip').length,
        error: parsed.filter(r => r.outcome === 'error').length,
        newClient: parsed.filter(r => r.outcome === 'new-client').length,
    }), [parsed]);

    const errorRows = parsed ? parsed.filter(r => r.outcome === 'error') : [];

    if (!parsed || !counts) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 'var(--spacer-L)', color: 'var(--text-muted)' }}>
                <div className="spinner-border spinner-border-sm" role="status" style={{ marginRight: 'var(--spacer-XS)' }} />
                {t(TK.importAppointmentsAnalysing)}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            {/* Summary bar */}
            <div style={{
                display: 'flex',
                gap: 'var(--spacer-S)',
                flexWrap: 'wrap',
                padding: 'var(--spacer-XS) var(--spacer-S)',
                background: 'var(--bg-light)',
                borderRadius: '6px',
                border: 'var(--border-thin) solid var(--appLigthGrey)',
                fontSize: '0.875rem',
            }}>
                <SummaryChip color="var(--appGreen)" count={counts.create} label={t(TK.importAppointmentsPreviewCreateCount)} />
                {counts.newClient > 0 && (
                    <SummaryChip color="var(--warning-color)" count={counts.newClient} label={t(TK.importAppointmentsPreviewNewClientCount)} />
                )}
                {counts.skip > 0 && (
                    <SummaryChip color="var(--text-muted)" count={counts.skip} label={t(TK.importAppointmentsPreviewSkipCount)} />
                )}
                {counts.error > 0 && (
                    <SummaryChip color="var(--danger-color)" count={counts.error} label={t(TK.importAppointmentsPreviewErrorCount)} />
                )}
            </div>

            {/* Preview table */}
            <div style={{ overflowY: 'auto', maxHeight: '50vh' }}>
                <StyledTable scrollable hover size="sm">
                    <thead>
                        <tr>
                            <th style={{ width: '2rem' }}>#</th>
                            <th>{t(TK.date)}</th>
                            <th>{t(TK.startTime)}</th>
                            <th>{t(TK.client)}</th>
                            <th>{t(TK.professional)}</th>
                            <th>{t(TK.service)}</th>
                            <th>{t(TK.room)}</th>
                            <th>{t(TK.price)}</th>
                            <th style={{ width: '6rem' }}>{t(TK.importAppointmentsPreviewOutcome)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {parsed.map(row => (
                            <PreviewRow key={row.rowIndex} row={row} options={options} generalSettings={generalSettings} t={t} />
                        ))}
                    </tbody>
                </StyledTable>
            </div>

            {/* Error list (capped at 20) */}
            {errorRows.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <p style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--danger-color)', margin: 0 }}>
                        {t(TK.importAppointmentsPreviewErrorCount)} ({errorRows.length})
                    </p>
                    {errorRows.slice(0, 20).map(row => (
                        <div
                            key={row.rowIndex}
                            style={{
                                fontSize: '0.8rem',
                                color: 'var(--danger-color)',
                                padding: 'var(--spacer-XXS) var(--spacer-XS)',
                                background: '#fff5f5',
                                borderRadius: '4px',
                                border: 'var(--border-thin) solid var(--danger-color)',
                            }}
                        >
                            <strong>{`Row ${row.rowIndex + 1}`}</strong>
                            {' — '}
                            {row.errors.map(e => t(ERROR_TK[e] ?? TK.error)).join(', ')}
                            {row.clientName ? ` (${row.clientName})` : ''}
                        </div>
                    ))}
                    {errorRows.length > 20 && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: 'var(--spacer-XXS) 0' }}>
                            {t(TK.importAppointmentsAndNMore).replace('{n}', String(errorRows.length - 20))}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

interface SummaryChipProps {
    color: string;
    count: number;
    label: string;
}

const SummaryChip: React.FC<SummaryChipProps> = ({ color, count, label }) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XXS)', color }}>
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '1.4rem',
            height: '1.4rem',
            borderRadius: '50%',
            background: color,
            color: 'white',
            fontWeight: 700,
            fontSize: '0.75rem',
        }}>{count}</span>
        {label}
    </span>
);

interface PreviewRowProps {
    row: ParsedAppointmentRow;
    options: ImportAppointmentOptions;
    generalSettings: ReturnType<typeof getGeneralSettings>;
    t: (key: TK) => string;
}

const PreviewRow: React.FC<PreviewRowProps> = ({ row, options, generalSettings, t }) => {
    const roomDisplay = resolveRoom(row.serviceKey, options, generalSettings, row.room);
    const isSkipped = row.outcome === 'skip';
    const isError = row.outcome === 'error';
    const rowStyle: React.CSSProperties = {
        opacity: isSkipped ? 0.5 : 1,
        background: isError ? '#fff5f5' : isSkipped ? 'var(--bg-light)' : undefined,
    };

    return (
        <tr style={rowStyle}>
            <td style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{row.rowIndex + 1}</td>
            <td style={{ whiteSpace: 'nowrap' }}>{row.date ?? <span style={{ color: 'var(--danger-color)' }}>—</span>}</td>
            <td style={{ whiteSpace: 'nowrap' }}>{row.time}</td>
            <td>
                {row.clientName ?? '—'}
                {row.isNewClient && (
                    <span style={{
                        marginLeft: 'var(--spacer-XXS)',
                        fontSize: '0.7rem',
                        color: 'var(--warning-color)',
                        fontWeight: 600,
                    }}>
                        {t(TK.importAppointmentsStatusNewClient)}
                    </span>
                )}
            </td>
            <td>{row.professionalName ?? '—'}</td>
            <td>{row.serviceLabel ?? '—'}</td>
            <td>{roomDisplay ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
            <td>{row.price != null ? row.price.toFixed(2) : '—'}</td>
            <td>
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--spacer-XXS)',
                    fontSize: '0.75rem',
                    color: OUTCOME_COLOR[row.outcome],
                    fontWeight: 500,
                }}>
                    <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: OUTCOME_COLOR[row.outcome],
                        flexShrink: 0,
                    }} />
                    {t(OUTCOME_TK[row.outcome])}
                </span>
            </td>
        </tr>
    );
};

export default AppointmentPreviewStep;
